/**
 * Audiometria estruturada (#461, spec §4): limiares por orelha e frequência, referência do
 * trabalhador e comparação. Médias, situação e piora vêm do servidor — a tela não recalcula.
 */

export const AUDIOMETRY_FREQUENCIES = [250, 500, 1000, 2000, 3000, 4000, 6000, 8000] as const;

export type AudiometryFrequency = (typeof AUDIOMETRY_FREQUENCIES)[number];

export const AUDIOMETRY_MIN_DB = -10;
export const AUDIOMETRY_MAX_DB = 120;

export type AudiometryEar = 'right' | 'left';

/** Chave de frequência como texto ("250" … "8000"); `null` = célula sem registro. */
export type EarThresholds = Partial<Record<`${AudiometryFrequency}`, number | null>>;

export interface AudiometryThresholds {
  right: EarThresholds;
  left: EarThresholds;
}

export type AudiometryComparisonStatus = 'WORSENED' | 'STABLE' | 'NOT_COMPARABLE' | 'BEFORE_REFERENCE';

export interface AudiometryFlaggedCell {
  ear: AudiometryEar;
  frequencyHz: number;
  deltaDb: number;
}

export interface AudiometryComparison {
  referenceExamId: number;
  /** `null` quando o exame de referência não tem data legível. */
  referenceDate: string | null;
  status: AudiometryComparisonStatus;
  /** Positivo quando piora. */
  delta: { right: EarThresholds; left: EarThresholds };
  flagged: AudiometryFlaggedCell[];
}

export interface AudiometryAverages {
  low: { right: number | null; left: number | null };
  high: { right: number | null; left: number | null };
}

export interface AudiometryRecord {
  appointmentExamId: number;
  thresholds: AudiometryThresholds | null;
  isReference: boolean;
  averages: AudiometryAverages;
  comparison: AudiometryComparison | null;
  /** Quando os limiares foram gravados pela última vez (ISO); ausente em respostas antigas. */
  savedAt?: string | null;
  savedBy?: { id: number; name: string } | null;
}

export type AudiometryRole = 'REFERENCE' | 'SEQUENTIAL' | 'BEFORE_REFERENCE' | 'NO_REFERENCE';

export interface AudiometryHistoryExam extends AudiometryRecord {
  appointmentId: number;
  /** Vazia quando o atendimento não tem data legível. */
  date: string;
  role: AudiometryRole;
}

export interface AudiometryReferenceInfo {
  appointmentExamId: number;
  /** `null` quando o exame de referência não tem data legível. */
  date: string | null;
  setBy: { id: number; name: string } | null;
  setAt: string;
}

export interface AudiometryHistory {
  reference: AudiometryReferenceInfo | null;
  effectiveReference: boolean;
  exams: AudiometryHistoryExam[];
}

export interface SaveAudiometryData {
  thresholds: AudiometryThresholds;
  setAsReference?: boolean;
}
